import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { Classes, ClassFilters } from '../models';
import * as classesService from '../services/classes.service';

export const useClasses = (defaultFilters: Partial<ClassFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<ClassFilters>>(otherDefaultFilters);

  const cacheData = classesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<ClassFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(classesService.classesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    classesService.classesQueryOptions(
      { search: { codeId: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: Classes['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
